// mailRecognitionRollup.ts — „Vertrag → Risiko → Kontakt" auf eine Aussage
// eindampfen.
//
// Die Erkennung liefert drei Ebenen, die dasselbe Anliegen beschreiben können:
// den Vertrag, sein Risikoobjekt und den Kontakt dahinter. Alle drei
// nebeneinander anzuzeigen ist Rauschen — der Vertrag ist die genaueste Aussage
// und trägt die anderen beiden implizit.
//
// Die Ausnahme ist der wichtige Teil: Gehört der erkannte Vertrag zu einem
// ANDEREN Kontakt als dem erkannten Absender, wird nichts zusammengefasst. Sonst
// verschwände genau die Information, dass hier zwei verschiedene Parteien im
// Spiel sind — etwa wenn jemand über den Vertrag eines Dritten schreibt.

import type { MailSenderCandidate } from "../../services/graphMailService";

export interface RollupContract {
    id: number;
    contactId: number | null;
    riskObjectIds?: number[];
}

export interface RollupRisk {
    id: number;
    contactId: number | null;
}

export interface RollupResult<C extends RollupContract, R extends RollupRisk> {
    contracts: C[];
    risks: R[];
    candidates: MailSenderCandidate[];
    /**
     * Der Vertrag gehört zu einem anderen Kontakt als dem erkannten Absender.
     * Dann bleibt alles stehen — und das Frontend kann darauf hinweisen.
     */
    contactMismatch: boolean;
}

/**
 * Gehört der Kandidat zu diesem Kontakt?
 *
 * Ein Ansprechpartner zählt als seine Firma: `resolveCandidateAssignment` bildet
 * ihn ohnehin auf sie ab. Ohne diese Gleichsetzung würde JEDE Mail eines
 * Ansprechpartners als Abweichung gelten und nie zusammengefasst.
 */
function candidateCovers(c: MailSenderCandidate, contactId: number): boolean {
    return c.contactId === contactId || c.viaCompanyId === contactId;
}

/**
 * Fasst die erkannten Entitäten zusammen.
 *
 * Regeln, in dieser Reihenfolge:
 *  1. Ohne erkannten Vertrag bleibt alles wie es ist — Risiko und Kontakt sind
 *     dann die genaueste vorhandene Aussage.
 *  2. Trägt ein Vertrag keinen Kontakt (`contactId === null`), wird nichts
 *     unterdrückt: Ein Vergleich gegen `null` würde sonst immer als Abweichung
 *     gelten oder — schlimmer — grundlos zusammenfassen.
 *  3. Deckt kein Kandidat den Vertrags-Kontakt ab, ist das die Abweichung:
 *     alles bleibt stehen, `contactMismatch` wird gesetzt.
 *  4. Sonst: Risiken, die an einem angezeigten Vertrag hängen, verschwinden;
 *     Kandidaten, die der Vertrag bereits abdeckt, ebenfalls.
 */
export function rollupRecognition<C extends RollupContract, R extends RollupRisk>(
    contracts: C[],
    risks: R[],
    candidates: MailSenderCandidate[],
): RollupResult<C, R> {
    const keep = { contracts, risks, candidates, contactMismatch: false };
    if (contracts.length === 0) return keep;

    const withContact = contracts.filter((c) => c.contactId != null);
    if (withContact.length === 0) return keep;

    // Bei mehreren Verträgen wird nur zusammengefasst, wenn sie ALLE zum selben
    // Kontakt gehören. Verschiedene Kontakte heißt: der Fall ist nicht eindeutig,
    // und dann ist Vollständigkeit wichtiger als Aufgeräumtheit.
    const contactIds = new Set(withContact.map((c) => c.contactId as number));
    if (contactIds.size > 1) return keep;

    const contractContactId = withContact[0].contactId as number;
    const covered = candidates.some((c) => candidateCovers(c, contractContactId));

    // Gibt es gar keine Kandidaten, liegt keine Abweichung vor — es gibt nur
    // nichts zu vergleichen. Der Vertrag darf trotzdem zusammenfassen.
    if (candidates.length > 0 && !covered) {
        return { ...keep, contactMismatch: true };
    }

    const fromContracts = new Set<number>();
    for (const c of contracts) for (const id of c.riskObjectIds ?? []) fromContracts.add(id);

    return {
        contracts,
        risks: risks.filter((r) => !fromContracts.has(r.id)),
        candidates: candidates.filter((c) => !candidateCovers(c, contractContactId)),
        contactMismatch: false,
    };
}
